import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'ui-components';
import {
  CHART_MIN_SCALE_MAX,
  CHART_STEPS,
  RETIREMENT_CALCULATOR_NAMESPACE,
  SAVINGS_PRODUCTS_ORDER,
  SAVINGS_PRODUCT_CHART_COLORS,
  ZUS_CHART_COLOR,
} from './RetirementCalculator.const';
import { PromotedProduct } from './RetirementCalculator.calculations';
import { RetirementChartSeriesModel, RetirementProjectionModel } from './RetirementCalculator.model';
import { getChartScaleTicks } from './RetirementCalculator.utils';

export const useRetirementChartSeries = (
  projection?: RetirementProjectionModel,
  promotedProduct?: PromotedProduct,
) => {
  const theme = useTheme();
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });

  const chartColors = theme.color.charts;
  const pension = projection?.monthlyPension;
  const savingsBreakdown = projection?.savingsBreakdown;
  const promotedPayout = promotedProduct ? (projection?.promotedProductPayout ?? 0) : 0;
  const barsCount = promotedProduct ? 3 : 2;
  const scaleTicks = getChartScaleTicks(
    (projection?.totalMonthlyPayout ?? pension ?? 0) + promotedPayout,
    CHART_MIN_SCALE_MAX,
    CHART_STEPS,
  );

  const series = useMemo<RetirementChartSeriesModel[]>(() => {
    if (pension === undefined) {
      return [];
    }

    const fill = (value: number, fromBar: number) =>
      Array.from({ length: barsCount }, (_, index) => (index < fromBar ? null : value));

    return [
      {
        key: 'zus',
        name: t('result.legend'),
        color: chartColors[ZUS_CHART_COLOR],
        values: fill(pension, 0),
      },
      ...SAVINGS_PRODUCTS_ORDER.filter(product => (savingsBreakdown?.[product] ?? 0) > 0).map(
        product => ({
          key: product,
          name: t(`savings.products.${product}`),
          color: chartColors[SAVINGS_PRODUCT_CHART_COLORS[product]],
          values: fill(savingsBreakdown?.[product] ?? 0, 1),
        }),
      ),
      ...(promotedProduct
        ? [
            {
              key: `promoted-${promotedProduct}`,
              name: t(`savings.products.${promotedProduct}`),
              color: chartColors[SAVINGS_PRODUCT_CHART_COLORS[promotedProduct]],
              values: fill(promotedPayout, 2),
            },
          ]
        : []),
    ];
  }, [barsCount, chartColors, pension, promotedPayout, promotedProduct, savingsBreakdown, t]);

  return { series, scaleTicks, barsCount };
};
